import { Carousel } from '@mantine/carousel';
import { Box, Center, Divider, Flex, Group, Stack, Text } from '@mantine/core';
import type { CSSProperties } from 'react';
import { IconLock } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AddCalendarsListComponent } from 'src/meetings/components/AddCalendarsListComponent';
import slideOneImg from 'src/meetings/assets/empty/slide-one.svg';
import slideTwoImg from 'src/meetings/assets/empty/slide-two.svg';
import carouselClasses from 'src/meetings/components/carousel.module.css';

type Props = {
  calendars: unknown[];
  isLoading: boolean;
};

export const MeetingsEmptyView = ({ calendars, isLoading }: Props) => {
  if (isLoading || calendars.length > 0) {
    return null;
  }

  return (
    <Flex h="100%" p="xxxxl" gap="xxxxl" justify="center">
      <Stack flex={1} gap="lg">
        <Stack gap="xs">
          <Text size="displayMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
            Turn client meetings
            <br /> into actions
          </Text>

          <Text size="md" fw={FONT_WEIGHTS.medium} c="gray.10">
            Connect Google, Microsoft, or Zoom — Afternoon will sync your meetings, record and
            transcribe them, draft meeting notes and suggest next steps automatically. Less admin,
            more time for clients.
          </Text>
        </Stack>

        <AddCalendarsListComponent showTitle={false} />

        <Divider color="gray.2" />

        <Group gap="sm" align="center">
          <Center
            w={40}
            h={40}
            bg="gray.0"
            style={{
              borderRadius: '50%',
              flexShrink: 0,
            }}
          >
            <IconLock size={20} stroke={1.5} color="var(--mantine-color-gray-6)" />
          </Center>
          <Text maw={420} size="sm" fw={FONT_WEIGHTS.medium} c="gray.6" style={{ flex: 1 }}>
            Afternoon only syncs your meetings.
            <br />
            There is no connection with your emails or existing document storage.
          </Text>
        </Group>
      </Stack>

      <Box
        flex={1}
        h="100%"
        mah={700}
        mih={600}
        miw={370}
        style={{
          borderRadius: '24px',
          background: 'linear-gradient(180deg, #FFF4EE 0%, #FFE4D2 100%)',
          overflow: 'hidden',
        }}
      >
        <Carousel
          withIndicators
          withControls={false}
          h="100%"
          emblaOptions={{ loop: true }}
          classNames={carouselClasses}
        >
          <CarouselPage
            title="Connect your calendar(s) and we’ll bring every meeting here for you."
            image={slideOneImg}
          />
          <CarouselPage
            title="Afternoon records, drafts meeting notes, extracts data and insights, and gives you suggestions."
            image={slideTwoImg}
          />
        </Carousel>
      </Box>
    </Flex>
  );
};

const imageStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  objectPosition: 'top center',
  display: 'block',
};

function CarouselPage({ title, image }: { title: string; image: string }) {
  return (
    <Carousel.Slide style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Text
        size="textMd"
        fw={FONT_WEIGHTS.medium}
        c="gray.10"
        ta="center"
        mt="xxxxl"
        mb="xxxl"
        mx="xxxl"
      >
        {title}
      </Text>

      <Box style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <img src={image} style={{ ...imageStyle, position: 'absolute', inset: 0 }} />
        <Box
          style={{
            background: 'linear-gradient(180deg, transparent 0%, #FFE6D5 100%)',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 110,
            zIndex: 1,
          }}
        />
      </Box>
    </Carousel.Slide>
  );
}
